// The socket, behind an interface.
//
// Connection is a state machine over an ordered byte stream and nothing else, so
// the WebSocket is injected rather than constructed. That is what lets the
// follower, the handshake, the heartbeat and the reconnect logic be tested
// against a scripted transport with no server and no browser, and it is also what
// lets the same client code drive a headless Node socket to prove itself against
// the real server.

export interface TransportHandlers {
  onOpen(): void;
  /** One whole binary message, in arrival order. */
  onMessage(b: Uint8Array): void;
  onClose(code: number, reason: string): void;
  onError(e: unknown): void;
}

export interface Transport {
  send(b: Uint8Array): void;
  close(code?: number, reason?: string): void;
}

export type TransportFactory = (url: string, h: TransportHandlers) => Transport;

/**
 * The default factory: a real WebSocket, binary frames.
 *
 * `binaryType = 'arraybuffer'` is not optional. The default in browsers is Blob,
 * whose only read path is asynchronous, which would put every frame through a
 * promise and lose the arrival ordering the whole protocol is built on.
 */
export const webSocketTransport: TransportFactory = (url, h) => {
  const ws = new WebSocket(url);
  ws.binaryType = 'arraybuffer';
  ws.addEventListener('open', () => h.onOpen());
  ws.addEventListener('message', (ev: MessageEvent) => {
    const d = ev.data;
    if (d instanceof ArrayBuffer) {
      h.onMessage(new Uint8Array(d));
    } else if (ArrayBuffer.isView(d)) {
      h.onMessage(new Uint8Array(d.buffer, d.byteOffset, d.byteLength));
    } else {
      // A text frame on a binary protocol. Not a message; report it and let the
      // connection decide, rather than trying to parse a string as a header.
      h.onError(new Error('net: server sent a non-binary frame'));
    }
  });
  ws.addEventListener('close', (ev: CloseEvent) => h.onClose(ev.code, ev.reason));
  ws.addEventListener('error', (e) => h.onError(e));
  return {
    send(b) {
      // Copy out of the pooled buffer: send is asynchronous underneath and a
      // caller reusing its writer would otherwise ship whatever it wrote next.
      ws.send(b.slice().buffer);
    },
    close(code, reason) {
      try {
        ws.close(code, reason);
      } catch {
        // Closing an already-closing socket is not an error worth propagating.
      }
    },
  };
};
